// Top 10 Jobs — the Job Analytics default view.
// ----------------------------------------------------------------------------
// Job Analytics is job-scoped: you pick a company, search for jobs, and each
// selected job gets its own panel. That leaves nothing on screen until you know
// which job you are looking for. This component fills that gap by ranking the
// ten busiest / riskiest jobs up front, so the page opens with an answer
// instead of a search box.
//
// Data: ONE call to /v1/forms/analytics WITHOUT `job_id`. The endpoint's job
// scoping is optional, so omitting it returns every form in scope and the
// per-job rollup happens client-side — one request instead of one per job.
//
// `companyid` is optional too. With no company picked the page opens on an
// all-companies ranking (the same scope the Safety Analytics dashboard uses),
// and narrows to one company once the dropdown is set. Job numbers are only
// unique within a company, so the rollup keys on company + job to stop two
// firms' job 100 merging into one bar.
//
// One ranking drives four panels. The metric switcher picks which ten jobs are
// "top", and every chart below re-cuts to that same list so they always
// describe the same jobs.
//
// WDS caveat: a weekly driver log spans up to 7 days and each day carries its
// own job. Without `job_id` the server cannot rebuild per-day attribution, so
// WDS forms here are counted against the job in the form header only. The
// per-job panels (which DO pass job_id) remain the accurate view for mileage
// and spend, and the UI carries a footnote saying so.

import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  LabelList,
} from "recharts";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import api from "../../api/api";
import {
  FORM_TYPE_META,
  FORM_CODES,
  METRICS,
  TOP_N,
  TREND_SERIES,
  rollupByJob,
  rankJobs,
  buildTrendSeries,
} from "./topJobsRollup";
import {
  ChartCard,
  JobTick,
  JobSafetyScorecard,
  JobIncidentsChart,
  JobActivityTrend,
} from "./TopJobsCharts";

const RankedBarTooltip = ({ active, payload, metric }) => {
  if (!active || !payload?.length) return null;
  const job = payload[0]?.payload;
  if (!job) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-lg">
      <div className="font-semibold text-[#0A0A0A]">{job.label}</div>
      {job.companyName && (
        <div className="mb-1 text-[11px] text-gray-500">{job.companyName}</div>
      )}
      <div className="mb-2" style={{ color: metric.color }}>
        {metric.label}: <b>{metric.format(metric.valueOf(job))}</b>
      </div>
      <div className="space-y-0.5">
        {FORM_TYPE_META.filter((f) => job[f.code] > 0).map((f) => (
          <div key={f.code} className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: f.color }}
            />
            <span className="text-gray-600">{f.label}</span>
            <span className="ml-auto font-medium text-[#0A0A0A]">{job[f.code]}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 border-t pt-1 text-[11px] text-gray-500">
        {job.observations > 0 && <>BBS concern {job.concernRate}% · </>}
        {job.failedAudits > 0 && <>{job.failedAudits} failed audit items · </>}
        {job.total} forms total
      </div>
    </div>
  );
};

// Bar length is always the selected metric so the chart reads top-to-bottom.
// Ranking by volume also stacks the form-type mix into the bars; the other
// metrics keep the mix in the tooltip, where it cannot distort the ordering.
const RankedJobsChart = ({ jobs, metric, onBarClick, clickable }) => {
  const stacked = metric.key === "forms";
  const barKey =
    metric.key === "risk"
      ? "riskScore"
      : metric.key === "concern"
      ? "concernRate"
      : "incidents";

  return (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart
        data={jobs}
        layout="vertical"
        margin={{ top: 4, right: 56, bottom: 34, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 10 }}
          allowDecimals={false}
          label={{
            value: stacked ? "# of forms" : metric.label,
            position: "insideBottom",
            offset: -12,
            fontSize: 10,
          }}
        />
        <YAxis type="category" dataKey="displayLabel" width={210} interval={0} tick={<JobTick />} />
        <Tooltip
          content={<RankedBarTooltip metric={metric} />}
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
        />
        {/* Top, not the default bottom: recharts puts the legend and the x-axis
            label in the same strip, and "# of forms" lands on top of the
            legend entries. */}
        {stacked && (
          <Legend verticalAlign="top" align="center" height={28} wrapperStyle={{ fontSize: 11 }} />
        )}

        {stacked ? (
          FORM_TYPE_META.map((f, i) => (
            <Bar
              key={f.code}
              dataKey={f.code}
              stackId="forms"
              name={f.label}
              fill={f.color}
              cursor={clickable ? "pointer" : undefined}
              onClick={onBarClick}
            >
              {/* Total sits past the end of the final segment. */}
              {i === FORM_TYPE_META.length - 1 && (
                <LabelList
                  dataKey="total"
                  position="right"
                  style={{ fontSize: 10, fill: "#4B5563" }}
                />
              )}
            </Bar>
          ))
        ) : (
          <Bar
            dataKey={barKey}
            name={metric.label}
            fill={metric.color}
            radius={[0, 4, 4, 0]}
            cursor={clickable ? "pointer" : undefined}
            onClick={onBarClick}
          >
            <LabelList
              position="right"
              style={{ fontSize: 10, fill: "#4B5563" }}
              formatter={(v) => metric.format(v)}
            />
          </Bar>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

const TopJobsOverview = ({ companyId, companyName, from, to, pmId, ready, onSelectJob }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [metricKey, setMetricKey] = useState("forms");

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];

  useEffect(() => {
    // `pmId` is derived from the signed-in user, which arrives from redux a beat
    // after mount. Firing before it lands would send the Project Manager's first
    // request with no pm_id and rank jobs from every company they cannot see, so
    // hold until the parent says the user is known.
    if (!ready) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const params = { from, to, form_types: FORM_CODES.join(",") };
        // Omitted entirely when no company is picked, which ranks every
        // company the user can see.
        if (companyId) params.companyid = companyId;
        if (pmId != null) params.pm_id = pmId;
        // No job_id: this is the cross-job feed the rollup needs.
        const resp = await api.get("/v1/forms/analytics", { params });
        if (cancelled) return;
        setRows(resp?.status === 200 && Array.isArray(resp.data) ? resp.data : []);
      } catch (err) {
        console.error("Top jobs overview fetch failed", err);
        if (!cancelled) {
          setRows([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, companyId, from, to, pmId]);

  const allJobs = useMemo(() => rollupByJob(rows), [rows]);

  // Highest first — what every panel except the horizontal bars wants.
  const rankedJobs = useMemo(() => {
    const ranked = rankJobs(allJobs, metric);
    // Job numbers repeat across companies, so an all-companies ranking can show
    // e.g. "6111 - Overhead Fuel" twice with no way to tell them apart. Only the
    // labels that actually clash get the company appended, so the rest stay
    // short enough to read on the axis.
    const seen = {};
    ranked.forEach((j) => {
      seen[j.label] = (seen[j.label] || 0) + 1;
    });
    return ranked.map((j) => ({
      ...j,
      displayLabel:
        seen[j.label] > 1 && j.companyName ? `${j.label} · ${j.companyName}` : j.label,
    }));
  }, [allJobs, metric]);
  // Recharts draws the first row at the BOTTOM of a vertical layout, so the
  // horizontal bar charts take the list reversed.
  const barJobs = useMemo(() => rankedJobs.slice().reverse(), [rankedJobs]);
  const trend = useMemo(() => buildTrendSeries(rankedJobs), [rankedJobs]);

  const summary = useMemo(() => {
    const forms = rankedJobs.reduce((n, j) => n + j.total, 0);
    const incidents = rankedJobs.reduce((n, j) => n + j.incidents, 0);
    const companies = new Set(
      rankedJobs.map((j) => j.companyName || j.companyid).filter(Boolean),
    ).size;
    return { forms, incidents, companies, jobs: allJobs.length };
  }, [rankedJobs, allJobs]);

  // With no company picked the ranking spans every company the user can see.
  // The name comes from the parent's dropdown so it is right while loading and
  // when the company has no forms in range; the feed is only a fallback.
  const scopeLabel = companyId
    ? companyName || rankedJobs[0]?.companyName || "Selected company"
    : "All companies";

  const handleBarClick = (entry) => {
    if (!onSelectJob || !entry?.jobId) return;
    // The per-job feed is company-scoped, so hand the company back too — the
    // parent needs it when the ranking was drawn across all companies.
    onSelectJob({
      job_num: entry.jobId,
      job_name: entry.jobName,
      companyid: entry.companyid,
    });
  };

  // Header + metric switcher render in every state so the controls never
  // disappear while a slow range loads.
  const header = (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-sm text-left font-bold uppercase tracking-wide text-[#0A0A0A]">
          Top {TOP_N} Jobs
        </h2>
        <p className="text-[11px] text-left font-medium text-[#717182]">{scopeLabel}</p>
        <p className="mt-0.5 text-[11px] text-left text-[#717182]">{metric.blurb}</p>
      </div>
      <div className="flex flex-wrap gap-1 rounded-xl bg-gray-100 p-1">
        {METRICS.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMetricKey(m.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
              m.key === metricKey
                ? "bg-white text-[#0A0A0A] shadow-sm"
                : "text-gray-500 hover:text-[#0A0A0A]"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (!ready || loading || failed || rankedJobs.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        {header}
        {!ready || loading ? (
          <div className="flex h-[420px] items-center justify-center text-xs text-gray-400">
            Loading job analytics…
          </div>
        ) : failed ? (
          <div className="flex h-[420px] items-center justify-center text-xs text-red-500">
            Could not load job analytics. Try changing the company or date range.
          </div>
        ) : (
          <div className="flex h-[420px] flex-col items-center justify-center text-center">
            <WorkOutlineIcon style={{ fontSize: 40 }} className="mb-2 text-gray-300" />
            <p className="text-sm text-gray-600">
              {allJobs.length === 0
                ? "No forms recorded in the selected range."
                : `No job has a ${metric.label.toLowerCase()} value to rank in this range.`}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Panel 1 — the ranking itself */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        {header}
        <RankedJobsChart
          jobs={barJobs}
          metric={metric}
          onBarClick={handleBarClick}
          clickable={!!onSelectJob}
        />

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 text-[11px] text-gray-500">
          <span>
            <b className="text-[#0A0A0A]">{summary.jobs}</b> jobs with activity ·{" "}
            <b className="text-[#0A0A0A]">{summary.forms}</b> forms in the top {TOP_N}
            {!companyId && summary.companies > 1 && (
              <>
                {" "}
                across <b className="text-[#0A0A0A]">{summary.companies}</b> companies
              </>
            )}
            {summary.incidents > 0 && (
              <>
                {" "}
                · <b className="text-[#0A0A0A]">{summary.incidents}</b> incidents
              </>
            )}
          </span>
          {onSelectJob && (
            <span className="ml-auto">
              Click a bar to open that job&apos;s full analytics.
            </span>
          )}
        </div>
        <p className="mt-1 text-left text-[10px] text-gray-400">
          Weekly Driver Log forms are counted against the job in the form header.
          Open a job for per-day mileage and spend.
        </p>
      </div>

      {/* Panels 2 & 3 — safety posture and incidents, side by side */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ChartCard
          title="Safety Scorecard"
          subtitle="Failed audit items per S&H form against BBS concern rate — which of these jobs are also unsafe."
        >
          <JobSafetyScorecard jobs={rankedJobs} />
        </ChartCard>

        <ChartCard
          title="Incidents by Severity"
          subtitle="Accident / incident reports for these jobs, split by classification severity."
        >
          <JobIncidentsChart jobs={rankedJobs} />
        </ChartCard>
      </div>

      {/* Panel 4 — activity over time */}
      <ChartCard
        title="Activity Trend"
        subtitle={`Monthly form activity for the leading ${TREND_SERIES} jobs — which are ramping up and which have gone quiet.`}
      >
        <JobActivityTrend trend={trend} />
      </ChartCard>
    </div>
  );
};

export default TopJobsOverview;
